import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, FolderOpen, Building, DollarSign, Star, Flag, Briefcase, BarChart3 } from 'lucide-react';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop, type ZijMenuItem } from './ZijMenu';

/**
 * Menu van de professional-werkomgeving. De cockpit-views wisselen binnen
 * /professional/cockpit; projecten en projectvoorstellen zijn eigen pagina's.
 * Eén definitie voor de cockpit én de losse werkpagina's (ProfessionalWerkSchil).
 */
export const PROFESSIONAL_COCKPIT_ITEMS = [
  { id: 'lopende_trajecten', labelKey: 'sidebar.trajecten', labelDefault: 'Trajecten', description: 'Actieve cliënt dossiers', icon: FolderOpen },
  { id: 'opdrachtgevers', labelKey: 'sidebar.employersPools', labelDefault: 'Opdrachtgevers & pools', description: 'Jouw actieve pools en tarieven', icon: Building },
  // Positie 3: 📅 Mijn Agenda Center (onderdeel 56C) — alle tijdsdomeinen in één matrix
  { id: 'agenda', labelKey: 'sidebar.agenda', labelDefault: 'Mijn Agenda Center', description: 'Beschikbaarheid, sessies, overleg en eigen tijd in één agenda', icon: Calendar },
  { id: 'financien', labelKey: 'sidebar.financien', labelDefault: 'Financiën', description: 'Urenstaten en self-billing facturen', icon: DollarSign },
  { id: 'reviews', labelKey: 'sidebar.reviews', labelDefault: 'Reviews', description: 'Jouw kwaliteitsbeoordelingen', icon: Star },
  // Alleen voor VTP en BMW; andere disciplines krijgen op de pagina een melding.
  { id: 'jaarverslag', labelKey: 'sidebar.jaarverslag', labelDefault: 'Case-registratie & jaarverslag', description: 'Vertrouwenspersoon en BMW: registreren, controleren en releasen', icon: BarChart3, href: '/professional/jaarverslag' },
  // Projecten draaien niet via de medewerkersagenda maar via mijlpalen met
  // een eigen fiat-flow; die staan daarom op een eigen pagina.
  { id: 'projecten', labelKey: 'sidebar.projecten', labelDefault: 'Projecten & mijlpalen', description: 'Uren schrijven op goedgekeurde mijlpalen', icon: Flag, href: '/professional/projecten' },
  { id: 'projectvoorstellen', labelKey: 'sidebar.projectvoorstellen', labelDefault: 'Mijn Projectvoorstellen', description: 'Gerichte aanvragen, voorstellen en de Model-OvO', icon: Briefcase, href: '/professional/projectvoorstellen' },
] as const;

/** Losse werkpagina's die de professional-schil krijgen, met het menu-item dat dan actief is. */
const PAD_NAAR_ITEM: Array<[string, string | null]> = [
  ['/professional/projectvoorstellen', 'projectvoorstellen'],
  ['/professional/projecten', 'projecten'],
  ['/professional/jaarverslag', 'jaarverslag'],
  ['/professional/agenda', 'agenda'],
  ['/professional/dossiers', 'lopende_trajecten'],
  ['/professional/meldingen', 'lopende_trajecten'],
  ['/professional/reports', 'lopende_trajecten'],
  ['/professional/notes', 'lopende_trajecten'],
  ['/professional/connections', 'opdrachtgevers'],
  ['/professional/search-requests', 'opdrachtgevers'],
  ['/professional/invoices', 'financien'],
  ['/professional/hours', 'financien'],
  ['/professional/adhoc-uren', 'financien'],
  ['/professional/facturen', 'financien'],
  ['/professional/uren', 'financien'],
  ['/professional/reviews', 'reviews'],
  ['/professional/action-items', null],
  ['/professional/documents', null],
  ['/professional/documenten', null],
];

/** Hoort dit pad in de professional-schil? Geeft het actieve item terug (of null). */
export function professionalSchilVoorPad(pathname: string): { actief: string | null } | undefined {
  const hit = PAD_NAAR_ITEM.find(([pad]) => pathname === pad || pathname.startsWith(pad + '/'));
  return hit ? { actief: hit[1] } : undefined;
}

export const ProfessionalWerkSchil: React.FC<{
  titel?: string;
  ondertitel?: string;
  label?: string;
  children: React.ReactNode;
}> = ({ titel, ondertitel, label, children }) => {
  const { t } = useTranslation('professional');
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const actief = professionalSchilVoorPad(location.pathname)?.actief ?? null;

  const items: ZijMenuItem[] = PROFESSIONAL_COCKPIT_ITEMS.map((item) => ({
    key: item.id,
    label: t(item.labelKey, { defaultValue: item.labelDefault }),
    omschrijving: item.description,
    icon: item.icon,
    href: 'href' in item ? item.href : `/professional/cockpit?view=${item.id}`,
    actief: actief === item.id,
  }));

  return (
    <div className="bg-gray-50">
      <ZijMenuMobielKnop onClick={() => setOpen(true)} />
      <div className="max-w-7xl mx-auto flex min-h-[calc(100vh-4rem)]">
        <ZijMenu
          titel={t('cockpit.cockpitTitle', { defaultValue: 'Mijn Cockpit' })}
          ondertitel={t('cockpit.welcomeDesc', { defaultValue: '' }) || undefined}
          groepen={[{ items }]}
          mobielOpen={open}
          onMobielSluit={() => setOpen(false)}
        />
        <div className="flex-1 min-w-0">
          {titel && <PaginaKop titel={titel} ondertitel={ondertitel} label={label} className="px-4 sm:px-6 pt-6" />}
          {children}
        </div>
      </div>
    </div>
  );
};
